/**
 * Asset Creation - Step 1: Select Type
 *
 * First step in the asset creation flow.
 * Allows user to select an asset type from the catalog.
 */

import { useCallback, useEffect, useRef } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { Alert, Text } from '@mantine/core'
import { IconBell } from '@tabler/icons-react'
import type { Id } from 'convex/_generated/dataModel'
import { useAssetCreation } from '@/features/assets/context/AssetCreationContext'
import { useTopLevelCatalog, useValidChildCatalog } from '@/features/assets/hooks/useAssetData'
import { AssetTypeStep } from '@/features/assets/components/AssetTypeStep'
import { useWorkflowNavigation } from '@/components/common'

export const Route = createFileRoute('/_authed/assets/new/')({
  component: SelectTypeStep,
})

function SelectTypeStep() {
  const navigate = useNavigate()
  // Read fromProject from URL without enforcing types (avoids breaking existing links)
  const rawSearch = useSearch({ strict: false })
  const fromProject = rawSearch.fromProject
  const { parentId, selectedCatalog, setSelectedCatalog } = useAssetCreation()
  const { updatePrimaryButton, updateSecondaryButton } = useWorkflowNavigation()

  // Use top-level catalog for root assets, valid children for sub-assets
  const { catalog: topLevelCatalog, isLoading: topLevelLoading } = useTopLevelCatalog()
  const { catalog: childCatalog, isLoading: childLoading } = useValidChildCatalog(
    parentId as Id<'assetInventory'> | undefined
  )

  // Select appropriate catalog based on context
  const catalog = parentId ? childCatalog : topLevelCatalog
  const catalogLoading = parentId ? childLoading : topLevelLoading

  const handleSelect = (item: typeof catalog[0]) => {
    setSelectedCatalog(item)
  }

  const handleNext = useCallback(() => {
    if (selectedCatalog) {
      navigate({ to: '/assets/new/configure', search: { parentId: parentId as string | undefined } })
    }
  }, [selectedCatalog, parentId, navigate])

  const handleCancel = useCallback(() => {
    if (parentId) {
      navigate({ to: '/assets/$id', params: { id: parentId as string } })
    } else {
      navigate({ to: '/assets' })
    }
  }, [parentId, navigate])

  // Use refs to store latest callbacks to avoid infinite loops
  const handleNextRef = useRef(handleNext)
  const handleCancelRef = useRef(handleCancel)
  handleNextRef.current = handleNext
  handleCancelRef.current = handleCancel

  // Configure navigation buttons
  useEffect(() => {
    updateSecondaryButton({
      label: 'Cancel',
      onClick: () => handleCancelRef.current(),
      variant: 'subtle',
    })

    updatePrimaryButton({
      label: 'Next',
      onClick: () => handleNextRef.current(),
      disabled: !selectedCatalog,
    })

    return () => {
      updateSecondaryButton(undefined)
      updatePrimaryButton(undefined)
    }
  }, [selectedCatalog, updatePrimaryButton, updateSecondaryButton])

  return (
    <>
      {fromProject && (
        <Alert
          icon={<IconBell size={16} />}
          color="primary"
          variant="light"
          mb="md"
          radius="md"
        >
          <Text size="sm">
            Great work finishing <strong>{fromProject}</strong>! Add your home below to get proactive maintenance reminders when things need attention.
          </Text>
        </Alert>
      )}
      <AssetTypeStep
        catalog={catalog}
        isLoading={catalogLoading}
        selectedId={selectedCatalog?._id}
        onSelect={handleSelect}
        title={parentId ? 'What type of sub-asset is this?' : 'What type of asset is this?'}
        emptyMessage={
          parentId
            ? 'No sub-asset types are available for this asset.'
            : 'No asset types available.'
        }
      />
    </>
  )
}
